import React, {ReactNode, useEffect} from 'react';
import {toast} from 'react-toastify';
import {useForm, UseFormReturn} from 'react-hook-form';
import {t} from '@lingui/core/macro';

import {INotifyChannel} from 'entities/notification';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import InputText from 'shared/ui/Input/InputText';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import MainModal from 'shared/ui/Modal/MainModal';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import {validateDescription} from 'features/profile/lib/validateDescription';
import {isTranslate} from 'shared/lib/lang';
import {INotifyChannelApi} from 'features/profile/model/notifyChannelApi';

import styles from './NotifyChannels.module.scss';

type EditFormValues = Record<string, string>;

interface INotifyChannelEditModalProps<T extends INotifyChannel> {
    item: T,
    api: INotifyChannelApi<T>,
    open: boolean,
    handleClose: () => void,
    header: string,
    successText: string,
    extraValues?: EditFormValues,
    renderExtraFields?: (_form: UseFormReturn<EditFormValues>) => ReactNode,
}

const trimValues = (values: EditFormValues): EditFormValues => Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, value.trim()]),
);

const NotifyChannelEditModal = <T extends INotifyChannel>({
    item,
    api,
    open,
    handleClose,
    header,
    successText,
    extraValues,
    renderExtraFields,
}: INotifyChannelEditModalProps<T>) => {
    const [update] = api.useUpdateMutation();
    const form = useForm<EditFormValues>({mode: 'onChange'});
    const {register, handleSubmit, control, formState: {errors}, reset} = form;

    const extraValuesKey = JSON.stringify(extraValues ?? {});

    useEffect(() => {
        if (open) {
            reset({
                ...JSON.parse(extraValuesKey),
                description: item.description || '',
            });
        }
    }, [open, reset, item.description, extraValuesKey]);

    const onSubmit = async(data: EditFormValues) => {
        const result = await update({id: item.id, ...trimValues(data)});
        if ('error' in result) return;
        toast.info(successText);
        handleClose();
    };

    return (
        <MainModal
            header={header}
            open={open}
            handleClose={handleClose}
            size="md"
            dialogClassName={isTranslate() ? styles.enBackground : styles.background}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <ModalBodyWrapper>
                    {renderExtraFields?.(form)}
                    <FormGroupWithError
                        label={t`Описание`}
                        error={errors.description?.message}
                    >
                        <InputText
                            control={control}
                            name="description"
                            placeholder={t`Краткое описание`}
                            register={register}
                            validate
                            rules={{
                                required: t`Описание обязательно для заполнения`,
                                validate: validateDescription,
                            }}
                        />
                    </FormGroupWithError>
                </ModalBodyWrapper>
                <ModalBlockButton
                    primaryButtonText={t`Сохранить`}
                    primaryButtonType="submit"
                    secondaryButtonOnClick={handleClose}
                />
            </form>
        </MainModal>
    );
};

export default NotifyChannelEditModal;
